import React, { useState, useEffect, useRef, useCallback } from 'react';
import { networkManager, UniverseMapData, ConstellationPreviewData } from '../../systems/NetworkManager';

const PALETTE = {
  DARK: '#0f1b26',
  DARKER: '#0a131c',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  TEAL_BRIGHT: '#3fd4d6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
  GREEN: '#4ade80',
  YELLOW: '#facc15',
  RED: '#ef4444',
};

interface UniverseMapOverlayProps {
  onClose: () => void;
  onZoomToConstellation: () => void;
}

interface ConstellationNode {
  id: string;
  x: number;
  y: number;
  name: string;
  tier: number;
  threatLevel: 'green' | 'yellow' | 'red';
}

interface Connection {
  from: string;
  to: string;
  costAtoB: number;
  costBtoA: number;
}

const overlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: PALETTE.DARKER,
  display: 'flex',
  flexDirection: 'column',
  fontFamily: "'JetBrains Mono', monospace",
  color: PALETTE.LIGHT,
  pointerEvents: 'auto',
};

const headerStyle: React.CSSProperties = {
  padding: '15px 20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${PALETTE.DIM}`,
  backgroundColor: PALETTE.DARK,
};

const titleStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.TEAL,
  fontWeight: 600,
  letterSpacing: '2px',
};

const closeButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: PALETTE.PANEL,
  color: PALETTE.LIGHT,
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '11px',
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  position: 'relative',
  display: 'flex',
  overflow: 'hidden',
};

const canvasContainerStyle: React.CSSProperties = {
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
};

const instructionsStyle: React.CSSProperties = {
  padding: '10px 20px',
  textAlign: 'center',
  color: PALETTE.DIM,
  fontSize: '11px',
  borderTop: `1px solid ${PALETTE.DIM}`,
  backgroundColor: PALETTE.DARK,
};

const legendStyle: React.CSSProperties = {
  position: 'absolute',
  top: 12,
  left: 12,
  backgroundColor: 'rgba(10, 19, 28, 0.85)',
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  padding: '10px 12px',
  fontSize: '11px',
  pointerEvents: 'none',
};

const tooltipStyle: React.CSSProperties = {
  position: 'absolute',
  backgroundColor: PALETTE.DARK,
  border: `1px solid ${PALETTE.TEAL}`,
  borderRadius: '4px',
  padding: '8px 12px',
  fontSize: '11px',
  color: PALETTE.LIGHT,
  pointerEvents: 'none',
  zIndex: 100,
  whiteSpace: 'pre-line',
};

const sidePanelStyle: React.CSSProperties = {
  width: '320px',
  backgroundColor: PALETTE.DARK,
  borderLeft: `1px solid ${PALETTE.DIM}`,
  padding: '20px',
  overflowY: 'auto',
  fontSize: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: '10px',
  color: PALETTE.TEAL,
  letterSpacing: '1.5px',
  marginBottom: '4px',
  textTransform: 'uppercase',
};

const CONSTELLATION_SPACING = 180;

function zoneTypeToThreatLevel(zoneType: string): 'green' | 'yellow' | 'red' {
  if (zoneType === 'green') return 'green';
  if (zoneType === 'yellow') return 'yellow';
  return 'red';
}

function threatColor(level: 'green' | 'yellow' | 'red'): string {
  if (level === 'green') return PALETTE.GREEN;
  if (level === 'yellow') return PALETTE.YELLOW;
  return PALETTE.RED;
}

// Stable per-constellation starfield background — seeded only by canvas size so it
// doesn't shimmer between renders. ~120 dots scaled to canvas.
function renderBackground(ctx: CanvasRenderingContext2D, width: number, height: number) {
  const pseudo = (n: number) => {
    const s = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
    return s - Math.floor(s);
  };
  ctx.fillStyle = PALETTE.DARKER;
  ctx.fillRect(0, 0, width, height);

  const dotCount = Math.floor((width * height) / 12000);
  for (let i = 0; i < dotCount; i++) {
    const x = pseudo(i + 1) * width;
    const y = pseudo(i + 1000) * height;
    const r = pseudo(i + 2000) * 1.2;
    const a = 0.1 + pseudo(i + 3000) * 0.35;
    ctx.fillStyle = `rgba(245, 232, 209, ${a})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function UniverseMapOverlay({ onClose, onZoomToConstellation }: UniverseMapOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [zoom, setZoom] = useState(1.0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, panX: 0, panY: 0 });
  const [dragMoved, setDragMoved] = useState(false);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);
  const [hoveredConstellation, setHoveredConstellation] = useState<string | null>(null);
  const [selectedConstellationId, setSelectedConstellationId] = useState<string | null>(null);
  const [preview, setPreview] = useState<ConstellationPreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const constellationCoords = networkManager.getConstellationCoords();
  const currentConstellation = { x: constellationCoords.x, y: constellationCoords.y };
  const [constellations, setConstellations] = useState<ConstellationNode[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);

  // Fetch the full universe graph on open.
  useEffect(() => {
    const handleUniverseData = (data: UniverseMapData) => {
      const constellationNodes: ConstellationNode[] = data.constellations.map((s) => ({
        id: `s_${s.x}_${s.y}`,
        x: s.x,
        y: s.y,
        name: s.name,
        tier: s.tier,
        threatLevel: zoneTypeToThreatLevel(s.zoneType),
      }));
      const connectionList: Connection[] = data.connections.map((c) => ({
        from: `s_${c.fromX}_${c.fromY}`,
        to: `s_${c.toX}_${c.toY}`,
        costAtoB: c.costAtoB,
        costBtoA: c.costBtoA,
      }));
      setConstellations(constellationNodes);
      setConnections(connectionList);
      setIsLoading(false);
    };

    const prevUniverse = networkManager.onUniverseMapData;
    networkManager.onUniverseMapData = handleUniverseData;
    networkManager.requestUniverseMapData();

    return () => {
      networkManager.onUniverseMapData = prevUniverse;
    };
  }, []);

  // Per-constellation preview handler. Only update if the response matches the
  // currently selected constellation — late responses for old clicks are ignored.
  useEffect(() => {
    const handleConstellationPreview = (data: ConstellationPreviewData) => {
      setPreview((prev) => {
        // Drop late responses if the user has already clicked elsewhere.
        const id = `s_${data.constellationX}_${data.constellationY}`;
        if (selectedConstellationId && id !== selectedConstellationId) return prev;
        return data;
      });
      setPreviewLoading(false);
    };
    const prev = networkManager.onConstellationPreviewData;
    networkManager.onConstellationPreviewData = handleConstellationPreview;
    return () => {
      networkManager.onConstellationPreviewData = prev;
    };
  }, [selectedConstellationId]);

  // Request the preview whenever the selected constellation changes.
  useEffect(() => {
    if (!selectedConstellationId) {
      setPreview(null);
      return;
    }
    const constellation = constellations.find((s) => s.id === selectedConstellationId);
    if (!constellation) return;
    setPreviewLoading(true);
    setPreview(null);
    networkManager.requestConstellationPreview(constellation.x, constellation.y);
  }, [selectedConstellationId, constellations]);

  // Main render pass.
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const cx = width / 2;
    const cy = height / 2;

    renderBackground(ctx, width, height);

    const toScreen = (sx: number, sy: number) => ({
      x: cx + sx * CONSTELLATION_SPACING * zoom + pan.x,
      y: cy - sy * CONSTELLATION_SPACING * zoom + pan.y,
    });

    const posMap = new Map<string, { x: number; y: number }>();
    for (const constellation of constellations) posMap.set(constellation.id, toScreen(constellation.x, constellation.y));

    // Edges. Drawn with a soft outer glow first, then the line, then optional
    // cost label at midpoint when zoom is large enough.
    for (const conn of connections) {
      const fromPos = posMap.get(conn.from);
      const toPos = posMap.get(conn.to);
      if (!fromPos || !toPos) continue;

      // Outer glow.
      ctx.strokeStyle = 'rgba(32, 165, 166, 0.18)';
      ctx.lineWidth = 6 * zoom;
      ctx.beginPath();
      ctx.moveTo(fromPos.x, fromPos.y);
      ctx.lineTo(toPos.x, toPos.y);
      ctx.stroke();

      // Inner line.
      ctx.strokeStyle = 'rgba(63, 212, 214, 0.55)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(fromPos.x, fromPos.y);
      ctx.lineTo(toPos.x, toPos.y);
      ctx.stroke();

      // Cost label at midpoint (round-trip avg).
      if (zoom >= 0.9) {
        const mx = (fromPos.x + toPos.x) / 2;
        const my = (fromPos.y + toPos.y) / 2;
        const cost = Math.round((conn.costAtoB + conn.costBtoA) / 2);
        ctx.fillStyle = 'rgba(10, 19, 28, 0.85)';
        const text = `${cost} cr`;
        ctx.font = `${Math.max(9, 10 * zoom)}px monospace`;
        const metrics = ctx.measureText(text);
        const pad = 4;
        ctx.fillRect(
          mx - metrics.width / 2 - pad,
          my - 7,
          metrics.width + pad * 2,
          14,
        );
        ctx.fillStyle = PALETTE.DIM;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, mx, my);
      }
    }

    // Constellation nodes.
    for (const constellation of constellations) {
      const pos = toScreen(constellation.x, constellation.y);
      const isCurrent = constellation.x === currentConstellation.x && constellation.y === currentConstellation.y;
      const isHovered = hoveredConstellation === constellation.id;
      const isSelected = selectedConstellationId === constellation.id;
      const color = threatColor(constellation.threatLevel);

      const baseSize = 17 + (4 - constellation.tier) * 3;
      const nodeSize = baseSize * zoom;

      // Halo for current constellation.
      if (isCurrent) {
        const haloGrad = ctx.createRadialGradient(pos.x, pos.y, nodeSize * 0.8, pos.x, pos.y, nodeSize * 2.5);
        haloGrad.addColorStop(0, 'rgba(63, 212, 214, 0.4)');
        haloGrad.addColorStop(1, 'rgba(63, 212, 214, 0)');
        ctx.fillStyle = haloGrad;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, nodeSize * 2.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Outer faint glow per node.
      const glowGrad = ctx.createRadialGradient(pos.x, pos.y, nodeSize, pos.x, pos.y, nodeSize * 1.8);
      glowGrad.addColorStop(0, `${color}55`);
      glowGrad.addColorStop(1, `${color}00`);
      ctx.fillStyle = glowGrad;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, nodeSize * 1.8, 0, Math.PI * 2);
      ctx.fill();

      // Filled node.
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, nodeSize, 0, Math.PI * 2);
      ctx.fill();

      // Selection ring.
      if (isSelected) {
        ctx.strokeStyle = PALETTE.TEAL_BRIGHT;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, nodeSize + 6, 0, Math.PI * 2);
        ctx.stroke();
      } else if (isHovered) {
        ctx.strokeStyle = PALETTE.LIGHT;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, nodeSize + 4, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Inner border (always).
      ctx.strokeStyle = isCurrent ? PALETTE.TEAL_BRIGHT : 'rgba(10, 19, 28, 0.6)';
      ctx.lineWidth = isCurrent ? 2 : 1;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, nodeSize, 0, Math.PI * 2);
      ctx.stroke();

      // Tier label inside node.
      ctx.fillStyle = PALETTE.DARKER;
      ctx.font = `bold ${Math.max(10, 12 * zoom)}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`T${constellation.tier}`, pos.x, pos.y);

      // Constellation name below.
      ctx.fillStyle = isCurrent ? PALETTE.TEAL_BRIGHT : (isSelected ? PALETTE.LIGHT : '#c8b89a');
      ctx.font = `${Math.max(9, 11 * zoom)}px monospace`;
      ctx.textBaseline = 'top';
      ctx.fillText(constellation.name, pos.x, pos.y + nodeSize + 6);
    }

    // Current-constellation indicator arrow above the node.
    const currentPos = toScreen(currentConstellation.x, currentConstellation.y);
    const arrowY = currentPos.y - 38 * zoom;
    ctx.fillStyle = PALETTE.TEAL_BRIGHT;
    ctx.beginPath();
    ctx.moveTo(currentPos.x, currentPos.y - 26 * zoom);
    ctx.lineTo(currentPos.x - 8 * zoom, arrowY);
    ctx.lineTo(currentPos.x + 8 * zoom, arrowY);
    ctx.closePath();
    ctx.fill();
  }, [zoom, pan, constellations, connections, currentConstellation.x, currentConstellation.y, hoveredConstellation, selectedConstellationId]);

  // Pick the constellation under a given canvas-relative point.
  const pickConstellation = useCallback(
    (mouseX: number, mouseY: number, rect: DOMRect): ConstellationNode | null => {
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      for (const constellation of constellations) {
        const sx = centerX + constellation.x * CONSTELLATION_SPACING * zoom + pan.x;
        const sy = centerY - constellation.y * CONSTELLATION_SPACING * zoom + pan.y;
        const dist = Math.sqrt((mouseX - sx) ** 2 + (mouseY - sy) ** 2);
        const nodeSize = (17 + (4 - constellation.tier) * 3) * zoom;
        if (dist < nodeSize + 4) return constellation;
      }
      return null;
    },
    [constellations, zoom, pan],
  );

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => {
      const newZoom = Math.max(0.5, Math.min(3.0, prev + delta));
      const ratio = newZoom / prev;
      setPan((p) => ({ x: p.x * ratio, y: p.y * ratio }));
      return newZoom;
    });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      setDragMoved(false);
      setDragStart({ x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y });
    },
    [pan],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();

      if (isDragging) {
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) setDragMoved(true);
        setPan({ x: dragStart.panX + dx, y: dragStart.panY + dy });
        return;
      }

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const hit = pickConstellation(mouseX, mouseY, rect);
      if (hit) {
        setHoveredConstellation(hit.id);
        setTooltip({
          x: mouseX,
          y: mouseY,
          text: `${hit.name}\nTier ${hit.tier} · ${hit.threatLevel.toUpperCase()}\nCoords (${hit.x}, ${hit.y})\nClick for details`,
        });
      } else {
        setHoveredConstellation(null);
        setTooltip(null);
      }
    },
    [isDragging, dragStart, pickConstellation],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      const wasDragging = isDragging;
      const moved = dragMoved;
      setIsDragging(false);
      setDragMoved(false);
      if (!wasDragging || moved) return;

      // Treat as a click on the constellation under the cursor.
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const hit = pickConstellation(mouseX, mouseY, rect);
      if (hit) {
        setSelectedConstellationId((prev) => (prev === hit.id ? null : hit.id));
      } else {
        setSelectedConstellationId(null);
      }
    },
    [isDragging, dragMoved, pickConstellation],
  );

  // Keyboard shortcuts.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'm' || e.key === 'M') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const selectedConstellation = selectedConstellationId ? constellations.find((s) => s.id === selectedConstellationId) : null;
  const isCurrentSelected =
    selectedConstellation !== null && selectedConstellation !== undefined &&
    selectedConstellation.x === currentConstellation.x && selectedConstellation.y === currentConstellation.y;

  return (
    <div style={overlayStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>UNIVERSE MAP</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={closeButtonStyle} onClick={onZoomToConstellation}>
            Constellation Map
          </button>
          <button style={closeButtonStyle} onClick={onClose}>
            Close (M/ESC)
          </button>
        </div>
      </div>

      <div style={bodyStyle}>
        <div
          ref={containerRef}
          style={canvasContainerStyle}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => { setIsDragging(false); setHoveredConstellation(null); setTooltip(null); }}
        >
          <canvas ref={canvasRef} style={{ display: 'block', cursor: isDragging ? 'grabbing' : 'grab' }} />

          {isLoading && (
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              color: PALETTE.TEAL, fontSize: '11px',
            }}>
              Loading universe data...
            </div>
          )}

          <div style={legendStyle}>
            <div style={{ marginBottom: 6, color: PALETTE.TEAL, letterSpacing: '1.5px' }}>THREAT LEVELS</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: PALETTE.GREEN }} />
              <span>Green &mdash; Safe</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: PALETTE.YELLOW }} />
              <span>Yellow &mdash; Caution</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: PALETTE.RED }} />
              <span>Red &mdash; Dangerous</span>
            </div>
          </div>

          {tooltip && (
            <div style={{ ...tooltipStyle, left: tooltip.x + 14, top: tooltip.y - 6 }}>
              {tooltip.text}
            </div>
          )}
        </div>

        {selectedConstellation && (
          <div style={sidePanelStyle}>
            <div>
              <div style={sectionLabelStyle}>Constellation</div>
              <div style={{ fontSize: '16px', color: PALETTE.LIGHT, marginBottom: 2 }}>
                {selectedConstellation.name}
              </div>
              <div style={{ fontSize: '11px', color: PALETTE.DIM }}>
                Coords ({selectedConstellation.x}, {selectedConstellation.y}) · {isCurrentSelected ? 'YOU ARE HERE' : 'Remote'}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 14 }}>
              <div>
                <div style={sectionLabelStyle}>Tier</div>
                <div style={{ fontSize: '14px' }}>T{selectedConstellation.tier}</div>
              </div>
              <div>
                <div style={sectionLabelStyle}>Threat</div>
                <div style={{ fontSize: '14px', color: threatColor(selectedConstellation.threatLevel) }}>
                  {selectedConstellation.threatLevel.toUpperCase()}
                </div>
              </div>
              {preview?.resourceBonus && (
                <div>
                  <div style={sectionLabelStyle}>Bonus</div>
                  <div style={{ fontSize: '14px', color: PALETTE.ORANGE }}>{preview.resourceBonus}</div>
                </div>
              )}
            </div>

            {previewLoading && (
              <div style={{ color: PALETTE.DIM, fontSize: '11px' }}>Loading details…</div>
            )}

            {preview?.error && (
              <div style={{ color: PALETTE.RED, fontSize: '11px' }}>{preview.error}</div>
            )}

            {preview && !preview.error && !previewLoading && (
              <>
                <div>
                  <div style={sectionLabelStyle}>Stations ({preview.stations?.length ?? 0})</div>
                  {(preview.stations ?? []).length === 0 ? (
                    <div style={{ color: PALETTE.DIM, fontSize: '11px' }}>—</div>
                  ) : (
                    (preview.stations ?? []).map((s, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '2px 0' }}>
                        <span>{s.name}</span>
                        <span style={{ color: PALETTE.DIM }}>T{s.tier}</span>
                      </div>
                    ))
                  )}
                </div>

                {(preview.systems?.length ?? 0) > 0 && (
                  <div>
                    <div style={sectionLabelStyle}>Systems ({preview.systems?.length ?? 0})</div>
                    {(preview.systems ?? []).map((sys, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '2px 0' }}>
                        <span>{sys.name}</span>
                        <span style={{ color: PALETTE.DIM }}>
                          {sys.starType ?? '—'} · {sys.planetCount}p
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {(preview.privateStationCount ?? 0) > 0 && (
                  <div style={{ fontSize: '11px', color: PALETTE.DIM }}>
                    Private stations: {preview.privateStationCount}
                  </div>
                )}

                <div>
                  <div style={sectionLabelStyle}>Gateways ({preview.gateways?.length ?? 0})</div>
                  {(preview.gateways ?? []).length === 0 ? (
                    <div style={{ color: PALETTE.DIM, fontSize: '11px' }}>None</div>
                  ) : (
                    (preview.gateways ?? []).map((g, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '2px 0' }}>
                        <span>&rarr; {g.targetName}</span>
                        <span style={{ color: PALETTE.TEAL }}>{g.travelCost} cr</span>
                      </div>
                    ))
                  )}
                </div>

                <div style={{ fontSize: '11px', color: PALETTE.DIM, marginTop: 'auto' }}>
                  Online players: <span style={{ color: PALETTE.LIGHT }}>{preview.onlinePlayers ?? 0}</span>
                </div>
              </>
            )}

            {isCurrentSelected && (
              <button style={{ ...closeButtonStyle, marginTop: 8 }} onClick={onZoomToConstellation}>
                Open Constellation Map
              </button>
            )}
          </div>
        )}
      </div>

      <div style={instructionsStyle}>
        Drag to pan · Scroll to zoom · Click constellation to inspect · M/ESC to close
      </div>
    </div>
  );
}

export default UniverseMapOverlay;
